import { useState, useEffect, useRef, useCallback } from 'react';
import { useHapticFeedback } from '@/shared/hooks/useHapticFeedback';
import { Layout } from '@/shared/components/Layout';
import { useAuthStore } from '@/stores/authStore';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { OnboardingOverlay } from '@/features/onboarding/OnboardingOverlay';
import { PaywallModal } from '@/features/subscription/PaywallModal';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/shared/lib/api';
import { captureError } from '@/shared/lib/monitoring';
import { useGuidedTourStore } from '@/features/guided-tour';
import { useOnTabActive } from '@/shared/components/MainTabsKeepAlive';

const ONBOARDING_KEY = '2enbota_onboarding_done';

const getCardsWord = (n: number) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 19) return 'карточек';
  if (m10 === 1) return 'карточка';
  if (m10 >= 2 && m10 <= 4) return 'карточки';
  return 'карточек';
};

const getDaysWord = (n: number) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 19) return 'дней';
  if (m10 === 1) return 'день';
  if (m10 >= 2 && m10 <= 4) return 'дня';
  return 'дней';
};

interface HeroGoalCardProps {
  answered: number;
  goal: number;
  streak: number;
  accuracy: number | null;
  xpGained: number;
  onClick: () => void;
}

function HeroGoalCard({
  answered,
  goal,
  streak,
  accuracy,
  xpGained,
  onClick,
}: HeroGoalCardProps) {
  const { selection, impact, notify } = useHapticFeedback();
  const circumference = 226.195;
  const targetPct = goal > 0 ? Math.min(100, Math.round((answered / goal) * 100)) : 0;
  const targetOffset = circumference * (1 - targetPct / 100);

  const [currentOffset, setCurrentOffset] = useState<number>(circumference);
  const [displayNumber, setDisplayNumber] = useState<number>(0);
  const rafRef = useRef<number | null>(null);
  const hasMountedRef = useRef(false);
  const prevAnsweredRef = useRef(answered);

  const isUltra = answered >= 100;
  const isOver = answered > goal && !isUltra;
  const isExact = answered === goal;

  const activeGradientUrl = isUltra
    ? 'url(#heroGaugeUltra)'
    : isOver
      ? 'url(#heroGaugeOver)'
      : isExact
        ? 'url(#heroGaugeSucc)'
        : 'url(#heroGaugeNorm)';

  const subtext = (isOver || isUltra)
    ? `+${answered - goal}`
    : (isExact ? 'цель' : `из ${goal}`);

  const runSweep = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const PAUSE_ZERO = 120;
    const REV_UP = 280;
    const PEAK_PAUSE = 60;
    const RECOIL = (answered > goal) ? 260 : 380;

    let startTime: number | null = null;

    // 1. Старт в 0 (пустое кольцо)
    setCurrentOffset(circumference);
    setDisplayNumber(0);

    const step = (now: number) => {
      if (!startTime) startTime = now;
      const elapsed = now - startTime;

      if (elapsed < PAUSE_ZERO) {
        setCurrentOffset(circumference);
        setDisplayNumber(0);
      } else if (elapsed < PAUSE_ZERO + REV_UP) {
        const t = (elapsed - PAUSE_ZERO) / REV_UP;
        const ease = 1 - Math.pow(1 - t, 2.5);
        setCurrentOffset(circumference * (1 - ease));
        setDisplayNumber(Math.round(ease * (answered > goal ? goal : answered)));
      } else if (elapsed < PAUSE_ZERO + REV_UP + PEAK_PAUSE) {
        setCurrentOffset(0);
        setDisplayNumber(answered > goal ? goal : answered);
      } else if (elapsed < PAUSE_ZERO + REV_UP + PEAK_PAUSE + RECOIL) {
        const t = (elapsed - (PAUSE_ZERO + REV_UP + PEAK_PAUSE)) / RECOIL;
        const ease = 1 - Math.pow(1 - t, 2.8);

        if (answered > goal) {
          setCurrentOffset(0);
          setDisplayNumber(goal + Math.round(ease * (answered - goal)));
        } else {
          const curPct = 100 - (100 - targetPct) * ease;
          setCurrentOffset(circumference * (1 - curPct / 100));
          setDisplayNumber(Math.round(answered * ease));
        }
      } else {
        // Завершение анимации
        setCurrentOffset(answered >= goal ? 0 : targetOffset);
        setDisplayNumber(answered);

        if (answered >= 100) {
          impact('medium');
        } else if (answered === goal) {
          notify('success');
        } else {
          selection();
        }
        return;
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
  }, [answered, goal, targetPct, targetOffset, circumference, impact, notify, selection]);

  // Запуск при первом монтировании
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      runSweep();
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [runSweep]);

  // Запуск при обновлении данных (например, когда пришла статистика с сервера)
  useEffect(() => {
    if (hasMountedRef.current && prevAnsweredRef.current !== answered) {
      prevAnsweredRef.current = answered;
      runSweep();
    }
  }, [answered, runSweep]);

  // Запуск при возврате на вкладку "Главная"
  useOnTabActive(() => {
    if (hasMountedRef.current) {
      runSweep();
    }
  });

  // Запуск при повторном клике по активному табу "Главная" в нижней панели
  useEffect(() => {
    const handleHomeTabTap = () => {
      runSweep();
    };
    window.addEventListener('2enbota:home-tab-tap', handleHomeTabTap);
    return () => {
      window.removeEventListener('2enbota:home-tab-tap', handleHomeTabTap);
    };
  }, [runSweep]);

  // Подготовка текстов для карточки
  const badgeText = isUltra
    ? '100+ слов • Ультра'
    : (isOver
      ? `+${answered - goal} к цели`
      : (isExact ? '100%' : `${targetPct}%`));

  const titleText = isUltra
    ? `⚡ Рекорд: ${answered} слов!`
    : (isOver
      ? `🔥 +${answered - goal} сверх нормы!`
      : (isExact
        ? '🎉 Дневная цель закрыта!'
        : (answered === 0
          ? `Цель дня: ${goal} карточек`
          : `Осталось ${goal - answered} ${getCardsWord(goal - answered)}`)));

  const descText = isUltra
    ? 'Космический результат! Топ среди студентов!'
    : (isOver
      ? 'Отличный темп, дневная норма перевыполнена!'
      : (isExact
        ? `Все ${goal} карточек закрыты. Серия сохранена!`
        : (answered === 0
          ? 'Сделай первый шаг — начни учёбу!'
          : `~${Math.max(1, Math.round((goal - answered) * 0.3))} мин. для закрытия цели`)));

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--theme-bg-card)',
        border: '1px solid var(--theme-border)',
        borderRadius: 18,
        padding: '16px 14px',
        marginBottom: 14,
        boxShadow: 'var(--theme-shadow)',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Круговой индикатор SVG */}
        <div style={{ position: 'relative', width: 84, height: 84, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="84" height="84" viewBox="0 0 90 90" style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
            <defs>
              <linearGradient id="heroGaugeNorm" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="var(--gauge-norm-1)" />
                <stop offset="100%" stopColor="var(--gauge-norm-2)" />
              </linearGradient>
              <linearGradient id="heroGaugeSucc" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="var(--gauge-succ-1)" />
                <stop offset="100%" stopColor="var(--gauge-succ-2)" />
              </linearGradient>
              <linearGradient id="heroGaugeOver" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="var(--gauge-over-1)" />
                <stop offset="100%" stopColor="var(--gauge-over-2)" />
              </linearGradient>
              <linearGradient id="heroGaugeUltra" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="var(--gauge-ultra-1)" />
                <stop offset="100%" stopColor="var(--gauge-ultra-2)" />
              </linearGradient>
            </defs>
            {/* Фоновый круг */}
            <circle
              cx="45"
              cy="45"
              r={36}
              stroke="var(--theme-border)"
              strokeWidth="7.5"
              fill="none"
              opacity="0.45"
            />
            {/* Активная градиентная дуга спидометра */}
            <circle
              cx="45"
              cy="45"
              r={36}
              stroke={activeGradientUrl}
              strokeWidth="7.5"
              strokeDasharray={circumference}
              strokeDashoffset={currentOffset}
              strokeLinecap="round"
              fill="none"
            />
          </svg>

          {/* Значения внутри кольца: Классика */}
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            pointerEvents: 'none',
            userSelect: 'none',
          }}>
            <span style={{
              fontSize: 21,
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              color: isUltra
                ? 'var(--gauge-ultra-1, #c084fc)'
                : (isOver
                  ? 'var(--gauge-over-2, #f59e0b)'
                  : (isExact
                    ? 'var(--theme-success, #00e676)'
                    : 'var(--theme-text)')),
              lineHeight: 1,
              letterSpacing: '-0.5px',
            }}>
              {displayNumber}
            </span>
            <span style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--theme-text-secondary)',
              marginTop: 3,
              lineHeight: 1,
              letterSpacing: '0.2px',
            }}>
              {subtext}
            </span>
          </div>
        </div>

        {/* Описание цели + метрики */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              color: 'var(--theme-text-muted)',
            }}>
              Цель на сегодня
            </span>
            <span style={{
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 10,
              fontVariantNumeric: 'tabular-nums',
              background: isUltra
                ? 'rgba(168, 85, 247, 0.22)'
                : (isOver
                  ? 'rgba(245, 158, 11, 0.18)'
                  : (isExact
                    ? 'rgba(0, 230, 118, 0.15)'
                    : 'rgba(124, 77, 255, 0.15)')),
              color: isUltra
                ? 'var(--gauge-ultra-1, #c084fc)'
                : (isOver
                  ? 'var(--gauge-over-2, #f59e0b)'
                  : (isExact
                    ? 'var(--theme-success, #00e676)'
                    : 'var(--theme-primary)')),
            }}>
              {badgeText}
            </span>
          </div>

          <div style={{
            fontSize: 15,
            fontWeight: 700,
            color: 'var(--theme-text)',
            marginTop: 3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {titleText}
          </div>

          <div style={{
            fontSize: 12,
            color: 'var(--theme-text-secondary)',
            marginTop: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {descText}
          </div>

          {/* Мини-метрики дня внизу карточки */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px solid var(--theme-border)',
            fontSize: 11,
            fontWeight: 600,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#ff6b35' }}>
              <span>🔥</span>
              <span>{streak} {getDaysWord(streak)}</span>
            </div>
            <span style={{ color: 'var(--theme-border)' }}>•</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'var(--theme-text-secondary)' }}>
              <span>🎯</span>
              <span>{accuracy != null ? `${accuracy}%` : '—'}</span>
            </div>
            <span style={{ color: 'var(--theme-border)' }}>•</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#ffd740' }}>
              <span>⚡</span>
              <span>+{xpGained} XP</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { is_premium } = useSubscriptionStore();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [todayStats, setTodayStats] = useState<any>(null);
  const [srsCount, setSrsCount] = useState<number | null>(null);
  const [addingTop100, setAddingTop100] = useState(false);

  useEffect(() => {
    // Показываем онбординг для новых пользователей
    if (!localStorage.getItem(ONBOARDING_KEY)) {
      // Небольшая задержка чтобы App успел инициализироваться
      const timer = setTimeout(() => setShowOnboarding(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const refreshStats = () => {
    Promise.allSettled([
      api.get<any>('/stats/daily').then(d => setTodayStats(d)).catch((err) => captureError(err, { context: 'HomePage.dailyStats' })),
      api.get<{ count: number }>('/study/srs/count').then((d) => setSrsCount(d?.count ?? 0)).catch((err) => captureError(err, { context: 'HomePage.srsCount' })),
    ]);
  };

  useEffect(() => {
    refreshStats();
  }, []);

  // Фоновая тихая валидация при каждом переходе на Главную
  useOnTabActive(refreshStats);

  const handleOnboardingDone = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    setShowOnboarding(false);
  };

  const ensureTop100Folder = async (): Promise<number | null> => {
    const FOLDER_NAME = '📚 Top 100 (A1)';
    try {
      const folders = await api.get<Array<{ id: number; name: string }>>('/folders');
      const list = Array.isArray(folders) ? folders : [];
      const existing = list.find((f: any) => f.name === FOLDER_NAME);
      if (existing) return existing.id;
      const created = await api.post<{ id: number }>('/folders', { name: FOLDER_NAME });
      return created?.id ?? null;
    } catch (err: any) {
      captureError(err, { context: 'HomePage.ensureTop100Folder' });
      return null;
    }
  };

  const handleAddTop100FromOnboarding = async () => {
    if (!window.confirm('Добавить 100 базовых слов (A1) в ваш словарь? Это поможет быстро начать учёбу.')) return;
    setAddingTop100(true);
    try {
      const coursesData = await api.get<Array<{ id: number; slug: string }>>('/courses');
      const courses = Array.isArray(coursesData) ? coursesData : [];
      const top100 = courses.find((c: any) => c.slug === 'top100');
      if (!top100) { alert('Курс top100 не найден'); return; }
      const folderId = await ensureTop100Folder();
      await api.post(`/courses/${top100.id}/add-words`, { count: 50, folderId });
      await api.post(`/courses/${top100.id}/add-words`, { count: 50, folderId });
      handleOnboardingDone();
      useGuidedTourStore.getState().startTour();
      navigate('/words');
    } catch (err: any) {
      captureError(err, { context: 'HomePage.handleAddTop100FromOnboarding' });
      alert(err.message || 'Ошибка добавления слов');
    } finally {
      setAddingTop100(false);
    }
  };

  const handleAddCustomWordFromOnboarding = () => {
    handleOnboardingDone();
    navigate('/words?add=true');
  };

  const accuracy = todayStats && todayStats.total_answers > 0
    ? Math.round((todayStats.correct_answers / todayStats.total_answers) * 100)
    : null;

  const answered = todayStats?.answered_cards ?? 0;
  const goal = todayStats?.daily_goal ?? 20;
  const streak = todayStats?.current_streak ?? 0;
  const xpGained = todayStats?.xp_gained ?? 0;
  const percent = Math.min(200, Math.round((answered / goal) * 100));
  const isComplete = answered >= goal;
  const isOverdrive = answered > goal;
  const remaining = Math.max(0, goal - answered);


  return (
    <Layout>
      <div style={{ padding: '20px 16px', paddingBottom: 100 }}>
        {/* Приветствие */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <h1 style={{ color: 'var(--theme-text)', fontSize: 22, margin: 0, fontWeight: 700 }}>
              Привет, {user?.first_name || 'друг'}! 👋
            </h1>
            <button
              onClick={() => navigate('/settings')}
              aria-label="Настройки"
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid var(--theme-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--theme-text-secondary)',
                cursor: 'pointer',
                transition: 'background 0.15s ease, color 0.15s ease, transform 0.1s ease',
                flexShrink: 0,
                outline: 'none',
              }}
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
          </div>
          <p style={{ color: 'var(--theme-text-secondary)', marginTop: 4, fontSize: 13 }}>
            {isComplete
              ? 'Дневная цель закрыта! Гештальт завершён 🎉'
              : (answered > 0
                ? `Осталось ${remaining} ${getCardsWord(remaining)} до цели на сегодня`
                : 'Продолжай учить английский каждый день')}
          </p>
        </div>

        {/* Карточка дневной цели (Hero Goal Ring со спидометром) */}
        <HeroGoalCard
          answered={answered}
          goal={goal}
          streak={streak}
          accuracy={accuracy}
          xpGained={xpGained}
          onClick={() => navigate('/stats')}
        />

        {/* SRS плашка */}
        {srsCount !== null && srsCount > 0 && (
          <Link to="/study?mode=srs" style={{ textDecoration: 'none', display: 'block', marginBottom: 12 }}>
            <div style={{
              background: 'linear-gradient(135deg, #ff6b35, #ff3d00)',
              borderRadius: 16, padding: '14px 16px',
              display: 'flex', alignItems: 'center', gap: 12,
              boxShadow: '0 4px 16px rgba(255, 107, 53, 0.25)',
            }}>
              <div style={{ fontSize: 32 }}>🔥</div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
                  {srsCount} слов готовы к повторению!
                </div>
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
                  Не дай им уйти в забвение
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Главные действия */}
        <div style={{ display: 'grid', gap: 10 }}>
          <Link to="/study" style={{ textDecoration: 'none' }}>
            <div style={{
              background: 'linear-gradient(135deg, var(--theme-primary), var(--theme-primary-hover))',
              borderRadius: 16, padding: '18px 20px',
              display: 'flex', alignItems: 'center', gap: 16,
              boxShadow: '0 4px 20px rgba(124, 77, 255, 0.25)',
            }}>
              <div style={{ fontSize: 36 }}>📚</div>
              <div>
                <div style={{ color: 'var(--theme-primary-text)', fontWeight: 700, fontSize: 17 }}>Учиться сейчас</div>
                <div style={{ color: 'var(--theme-primary-text)', opacity: 0.8, fontSize: 13 }}>
                  Карточки, аудирование и диктант
                </div>
              </div>
            </div>
          </Link>

          {/* 4 плитки с аккуратными подложками карточек */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Link to="/words" style={{ textDecoration: 'none' }}>
              <div style={{
                background: 'var(--theme-bg-card)',
                border: '1px solid var(--theme-border)',
                borderRadius: 16,
                padding: '16px',
                height: '100%',
              }}>
                <div style={{ fontSize: 28 }}>📝</div>
                <div style={{ color: 'var(--theme-text)', fontWeight: 600, marginTop: 6, fontSize: 15 }}>
                  Слова
                </div>
                <div style={{ color: 'var(--theme-text-secondary)', fontSize: 12, marginTop: 2 }}>
                  Добавить и управлять
                </div>
              </div>
            </Link>

            <Link to="/grammar" style={{ textDecoration: 'none' }}>
              <div style={{
                background: 'var(--theme-bg-card)',
                border: '1px solid var(--theme-border)',
                borderRadius: 16,
                padding: '16px',
                height: '100%',
                position: 'relative',
              }}>
                {!is_premium && (
                  <div style={{
                    position: 'absolute', top: 10, right: 10,
                    background: '#FFD700', color: '#000',
                    fontSize: 9, padding: '2px 5px', borderRadius: 4, fontWeight: 700,
                  }}>PRO</div>
                )}
                <div style={{ fontSize: 28 }}>📖</div>
                <div style={{ color: 'var(--theme-text)', fontWeight: 600, marginTop: 6, fontSize: 15 }}>
                  Грамматика
                </div>
                <div style={{ color: 'var(--theme-text-secondary)', fontSize: 12, marginTop: 2 }}>
                  40+ тем
                </div>
              </div>
            </Link>

            <Link to="/courses" style={{ textDecoration: 'none' }}>
              <div style={{
                background: 'var(--theme-bg-card)',
                border: '1px solid var(--theme-border)',
                borderRadius: 16,
                padding: '16px',
                height: '100%',
              }}>
                <div style={{ fontSize: 28 }}>🎓</div>
                <div style={{ color: 'var(--theme-text)', fontWeight: 600, marginTop: 6, fontSize: 15 }}>
                  Курсы
                </div>
                <div style={{ color: 'var(--theme-text-secondary)', fontSize: 12, marginTop: 2 }}>
                  A1–C2, Top 100/500
                </div>
              </div>
            </Link>

            <Link to="/stats" style={{ textDecoration: 'none' }}>
              <div style={{
                background: 'var(--theme-bg-card)',
                border: '1px solid var(--theme-border)',
                borderRadius: 16,
                padding: '16px',
                height: '100%',
              }}>
                <div style={{ fontSize: 28 }}>📈</div>
                <div style={{ color: 'var(--theme-text)', fontWeight: 600, marginTop: 6, fontSize: 15 }}>
                  Статистика
                </div>
                <div style={{ color: 'var(--theme-text-secondary)', fontSize: 12, marginTop: 2 }}>
                  Прогресс и серия
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Premium промо для free-пользователей */}
        {!is_premium && (
          <button onClick={() => setShowPaywall(true)} style={{
            width: '100%', marginTop: 14, padding: '14px 16px',
            background: 'rgba(255, 215, 0, 0.08)', border: '1px solid rgba(255, 215, 0, 0.25)',
            borderRadius: 16, display: 'flex', alignItems: 'center',
            gap: 12, cursor: 'pointer',
          }}>
            <div style={{ fontSize: 28 }}>⭐</div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: '#FFD700', fontWeight: 700, fontSize: 15 }}>
                Попробуй Premium
              </div>
              <div style={{ color: 'var(--theme-text-secondary)', fontSize: 12 }}>
                Все курсы, безлимитный AI, RPG предметы
              </div>
            </div>
          </button>
        )}
      </div>

      {showOnboarding && (
        <OnboardingOverlay
          onDone={handleOnboardingDone}
          onAddTop100={handleAddTop100FromOnboarding}
          onAddCustomWord={handleAddCustomWordFromOnboarding}
        />
      )}
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
    </Layout>
  );
}
